<template>
  <div class="cover-image-input">
    <div class="cii-row">
      <input
        :value="modelValue"
        type="url"
        class="input"
        :maxlength="maxlength"
        placeholder="https://..."
        @input="onUrlInput"
      />
      <button type="button" class="btn-upload" :disabled="uploading" @click="triggerUpload">
        {{ uploading ? $t('bo.common.uploading') : $t('bo.common.upload') }}
      </button>
      <input ref="fileInput" type="file" accept="image/*" class="file-hidden" @change="onFileSelected" />
    </div>

    <div v-if="error" class="cii-error">{{ error }}</div>

    <div v-if="modelValue" class="cii-preview">
      <img :src="modelValue" alt="" @error="previewFailed = true" v-show="!previewFailed" />
      <button type="button" class="btn-clear" @click="clear">
        {{ $t('bo.common.remove') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: String, default: '' },
  mediaId: { type: [Number, String], default: null },
  maxlength: { type: [Number, String], default: 500 },
})
const emit = defineEmits(['update:modelValue', 'update:mediaId'])

const fileInput = ref(null)
const uploading = ref(false)
const error = ref('')
const previewFailed = ref(false)

watch(
  () => props.modelValue,
  () => {
    previewFailed.value = false
  }
)

/** Reject bare bucket roots like https://host/minio-s3/ (no object key). */
const isUsableImageUrl = (value) => {
  const url = typeof value === 'string' ? value.trim() : ''
  if (!url) return false
  try {
    const parsed = new URL(url)
    const segments = (parsed.pathname || '')
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean)
    return segments.length >= 2
  } catch {
    return false
  }
}

const setUrl = (url, id = null) => {
  emit('update:modelValue', url || '')
  emit('update:mediaId', id)
}

const clear = () => setUrl('', null)

const onUrlInput = (e) => {
  // Manual edit of the URL disconnects any previous Media id.
  setUrl(e.target.value, null)
}

const triggerUpload = () => fileInput.value?.click()

const onFileSelected = async (e) => {
  const file = e.target.files?.[0]
  if (!file) return
  e.target.value = ''
  error.value = ''
  previewFailed.value = false
  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('media', file)
    formData.append('file_name', file.name)
    formData.append('file_type', 'IMAGE')
    const { data } = await api.post('/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    const fileLink = data?.file_link ?? data?.data?.file_link
    const mediaId = data?.id ?? data?.data?.id ?? null
    if (!isUsableImageUrl(fileLink)) {
      error.value = t('bo.common.uploadFailed')
      return
    }
    setUrl(fileLink, mediaId)
  } catch (err) {
    error.value = err.response?.data?.message || t('bo.common.uploadFailed')
  } finally {
    uploading.value = false
  }
}
</script>

<style scoped>
.cover-image-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cii-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  box-sizing: border-box;
  background: white;
}

.input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-upload {
  padding: 0.5rem 0.9rem;
  border: 1px solid #3b82f6;
  background: white;
  color: #2563eb;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.btn-upload:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.file-hidden {
  display: none;
}

.cii-error {
  font-size: 0.8rem;
  color: #dc2626;
}

.cii-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.cii-preview img {
  height: 3rem;
  width: auto;
  max-width: 8rem;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
}

.btn-clear {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.8rem;
  cursor: pointer;
  text-decoration: underline;
}
</style>
